import { styled } from "goober";
import type { ReactNode } from "react";
import type { IconName } from "../Icon";
import { ResponsiveTabs } from "../ResponsiveTabs";
import { PanelHeader, PanelHeaderActions } from "../PanelHeader";
import { PanelFrame } from "../PanelFrame";
import { ChatpadToggleButton } from "./ChatpadToggleButton";

export type ChatpadSection = "outputs" | "sources";

const Frame = styled("div")`
  --chatpad-background: var(--surface-primary);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--chatpad-background);
  border-left: 0.5px solid var(--border-alpha);
`;

const Actions = styled(PanelHeaderActions)`
  --cell-bg: var(--surface-primary);
`;

const ContentArea = styled("div")`
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 0 12px 12px;
`;

const Content = styled(PanelFrame)`
  --chatpad-background: var(--surface-secondary);
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

const sections: { id: ChatpadSection; label: string; icon: IconName }[] = [
  { id: "outputs", label: "Files", icon: "folder" },
  { id: "sources", label: "Sources", icon: "earth" },
];

export function Chatpad({
  open,
  onToggle,
  activeSection,
  onSelectSection,
  connectors,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  activeSection: ChatpadSection;
  onSelectSection: (section: ChatpadSection) => void;
  connectors?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Frame>
      <PanelHeader role="navigation" aria-label="Side panel navigation">
        <ResponsiveTabs
          label="Chat panel views"
          activeKey={activeSection}
          items={sections.map((section) => ({
            key: section.id,
            label: section.label,
            icon: section.icon,
            onSelect: () => onSelectSection(section.id),
          }))}
        />
        <Actions>
          {connectors}
          <ChatpadToggleButton isOpen={open} onToggle={onToggle} />
        </Actions>
      </PanelHeader>
      <ContentArea>
        <Content>{children}</Content>
      </ContentArea>
    </Frame>
  );
}
